/*
  UI state for the Streamline solution screen, shared by web and mobile so both
  apps mark cards, switch solutions and collapse the summary through the same
  reducers.

  Only what the user did lives here, plus the set the screen is showing.
  Points, progress, cost and completion are derived on read by
  `streamlineTotals` — storing them would leave two copies of the same fact to
  keep in step.
*/
import { createAsyncThunk, createSlice, } from "@reduxjs/toolkit";
import { getStreamlinedOptions } from "../../api/sbcs/getStreamlinedOptions";
import { solveStreamlinedSbc, } from "../../api/sbcs/solveStreamlinedSbc";
import { backendErrorCode } from "../../i18n/errorMessage";
import { streamlineRatings, toStreamlineOptions, toStreamlineSbcSet, } from "../../streamline/adapter";
import { completedEaPlayerIds, completedMarks, completedPoints, } from "../../streamline/completed";
import { bracketEntryIds, isBracketDone } from "../../streamline/totals";
/** The backend's answer to a signed-out caller past the daily solve cap. */
export const ANONYMOUS_SOLVE_LIMIT_CODE = "ANONYMOUS_SOLVE_LIMIT_REACHED";
const initialState = {
    set: null,
    setStatus: "idle",
    error: null,
    solutionId: null,
    marks: {},
    swaps: {},
    alternativesOpen: false,
    earnedPoints: 0,
    completed: [],
    options: {},
    optionsStatus: "idle",
};
/* A solve that lands faster than this still shows its skeletons for this
   long: a flash of placeholders reads as a glitch, a beat reads as work. */
const MIN_SOLVE_MS = 1000;
/** Swap options for the ratings a set asks for. Best effort: the sheet shows what arrived. */
export const fetchStreamlineOptions = createAsyncThunk("streamline/fetchOptions", async ({ ratings, sbcId }) => toStreamlineOptions(await getStreamlinedOptions(ratings, sbcId)));
/**
 * Solutions for one set, adapted into the shape the screen renders. The
 * options are asked for straight after, so they are in hand before the user
 * taps a card.
 *
 * The solver is told the item score already earned: the loose figure
 * plus every added card's points, and which club cards those are.
 *
 * Settles no sooner than `MIN_SOLVE_MS` after it started, success or failure.
 */
export const fetchStreamlineSolution = createAsyncThunk("streamline/fetchSet", async ({ setId, sbcId, earnedPoints = 0, completed = [], settings }, { rejectWithValue, dispatch }) => {
    const floor = new Promise((resolve) => setTimeout(resolve, MIN_SOLVE_MS));
    try {
        const set = {
            ...toStreamlineSbcSet(await solveStreamlinedSbc(setId, {
                ...settings,
                sbcId,
                earnedPoints: earnedPoints + completedPoints(completed),
                completedEaPlayerIds: completedEaPlayerIds(completed),
            })),
            sbcId,
        };
        void dispatch(fetchStreamlineOptions({ ratings: streamlineRatings(set), sbcId }));
        await floor;
        return set;
    }
    catch (error) {
        const response = error?.response;
        /* The anonymous daily cap is a bare ForbiddenException: its name
           arrives as the message, not a code, so it is promoted to one here. */
        const code = backendErrorCode(error) ??
            (response?.status === 403 &&
                response.data?.error?.message === ANONYMOUS_SOLVE_LIMIT_CODE
                ? ANONYMOUS_SOLVE_LIMIT_CODE
                : undefined);
        await floor;
        return rejectWithValue({ status: response?.status, code });
    }
});
const streamlineSlice = createSlice({
    name: "streamline",
    initialState,
    reducers: {
        /*
          Switching alternatives resets progress: the marks are keyed to the
          solution that produced them and mean nothing against a different squad.
          The added cards are in every alternative, so they stay marked.
        */
        selectSolution: (state, action) => {
            if (state.solutionId === action.payload) {
                state.alternativesOpen = false;
                return;
            }
            state.solutionId = action.payload;
            state.marks = completedMarks(state.completed);
            state.swaps = {};
            state.alternativesOpen = false;
        },
        /*
          Added cards without a re-solve: the target is already reached, so there is
          nothing left to solve for. Marks and swaps stay; the new cards join them.
        */
        setCompleted: (state, action) => {
            state.completed = action.payload;
            Object.assign(state.marks, completedMarks(action.payload));
        },
        /** The loose figure alone, for a typed total the solver need not see. */
        setEarnedPoints: (state, action) => {
            state.earnedPoints = action.payload;
        },
        /* Marks or clears every card in a row; a row is done once every card is. */
        toggleBracket: (state, action) => {
            const bracket = action.payload;
            const next = !isBracketDone(bracket, state.marks);
            for (const entryId of bracketEntryIds(bracket)) {
                if (next)
                    state.marks[entryId] = true;
                else
                    delete state.marks[entryId];
            }
        },
        /** Marks or clears one card. */
        toggleEntry: (state, action) => {
            const entryId = action.payload;
            if (state.marks[entryId])
                delete state.marks[entryId];
            else
                state.marks[entryId] = true;
        },
        /*
          Point a slot at another card, or back at its own with null. A marked
          slot is never swapped: the screen offers no swap for one.
        */
        /* Named for the feature: the SBC solver already exports a `swapPlayer`
           through the same barrel. */
        swapStreamlinePlayer: (state, action) => {
            const { entryId, option } = action.payload;
            if (option === null)
                delete state.swaps[entryId];
            else
                state.swaps[entryId] = option;
        },
        setAlternativesOpen: (state, action) => {
            state.alternativesOpen = action.payload;
        },
        /** Called when the screen unmounts, so the next set starts clean. */
        resetStreamline: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            /* The set stays on screen while a re-solve is in flight so the layout
               holds; the new rows make the old marks and swaps meaningless. The
               added cards carried in the request are the ones that survive. */
            .addCase(fetchStreamlineSolution.pending, (state, action) => {
            state.setStatus = "pending";
            state.error = null;
            state.earnedPoints = action.meta.arg.earnedPoints ?? 0;
            state.completed = action.meta.arg.completed ?? [];
            state.marks = completedMarks(state.completed);
            state.swaps = {};
        })
            /* Every solve opens on its own recommended solution, whatever was
               showing before: the hook adopts it when no id is selected. */
            .addCase(fetchStreamlineSolution.fulfilled, (state, action) => {
            state.set = action.payload;
            state.setStatus = "fulfilled";
            state.solutionId = null;
        })
            .addCase(fetchStreamlineSolution.rejected, (state, action) => {
            state.setStatus = "rejected";
            state.error = action.payload ?? {};
        })
            /* The old options stay while new ones load: a re-solve mostly asks
               for the same ratings. */
            .addCase(fetchStreamlineOptions.pending, (state) => {
            state.optionsStatus = "pending";
        })
            .addCase(fetchStreamlineOptions.fulfilled, (state, action) => {
            state.options = action.payload;
            state.optionsStatus = "fulfilled";
        })
            .addCase(fetchStreamlineOptions.rejected, (state) => {
            state.optionsStatus = "rejected";
        });
    },
});
export const { selectSolution, toggleBracket, toggleEntry, swapStreamlinePlayer, setAlternativesOpen, setCompleted, setEarnedPoints, resetStreamline, } = streamlineSlice.actions;
export default streamlineSlice.reducer;
export const streamlineReducer = streamlineSlice.reducer;
export const streamlineStateSelector = (state) => state.streamlineReducer;
export const streamlineSetSelector = (state) => state.streamlineReducer.set ?? undefined;
//# sourceMappingURL=streamlineSlice.js.map